/**
 * Tag-provider flat-browse extractors and mappers.
 *
 *  - `coalesceTagFolder(tag)` reads the `folder` straight off the tag
 *    when present, falling back to the legacy `folder_name` property
 *    Ignition ingest still writes (see
 *    `packages/ontology/serdes/src/mappers/tag-mapper.ts`).
 *  - `mapPlantProviderTagsPage(raw)` projects the single-row response
 *    from `PLANT_PROVIDER_TAGS_CYPHER` into the typed
 *    {@link PlantProviderTagsPage} (tag list + page-stable total).
 *
 * Pure functions, no RTK / no Neo4j driver — trivially unit-testable.
 */

import { extractNodeList, toIntCount } from '../graph-row'
import type { CypherReadResult, PlantProviderTag, PlantProviderTagsPage } from '../types'

/**
 * Coalesce `folder` from the legacy `folder_name` property used by
 * `packages/ontology/serdes/src/mappers/tag-mapper.ts` — the Ignition
 * ingest still writes the ScadaTag-era field name, so a plain
 * `tag.folder` read returns null for Ignition-sourced tags. AVEVA
 * ingest writes `folder` directly, so its rows pass through unchanged.
 * Mirrors the same alias pattern `normalizeComputeHost()` applies to
 * the `Compute.url` / `gateway_url` rename.
 */
export function coalesceTagFolder(tag: PlantProviderTag): PlantProviderTag {
  if (tag.folder) return tag
  const legacy = (tag as unknown as Record<string, unknown>)['folder_name']
  if (typeof legacy === 'string' && legacy.length > 0) {
    return { ...tag, folder: legacy }
  }
  return tag
}

/**
 * Project the single row returned by `PLANT_PROVIDER_TAGS_CYPHER` into
 * the typed {@link PlantProviderTagsPage}. `tags` is the just-fetched
 * page; `totalTags` is the filter-aware total so the pager can render
 * "Page X of Y" stably across pages.
 */
export function mapPlantProviderTagsPage(raw: CypherReadResult): PlantProviderTagsPage {
  const row = raw.results[0] ?? {}
  const tags = extractNodeList<PlantProviderTag>(row['tags']).map(coalesceTagFolder)
  return { tags, totalTags: toIntCount(row['totalTags']) }
}
